// src/components/BusManager.js

import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axiosConfig'; // Assuming correct path
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
    DialogTitle, TextField, Typography, MenuItem, Select, InputLabel,
    FormControl, CircularProgress, Snackbar, Alert, IconButton, Tooltip,
    Stack,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    alpha,
    AlertTitle,
    Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Edit as EditIcon, DeleteOutline as DeleteIcon,
    DirectionsBus as DirectionsBusIcon, Add as AddIcon,
    WarningAmber as WarningIcon,
} from '@mui/icons-material';
import NoResultsImage from '../../assets/no-results.png'; // Assuming correct path
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';

const ListCard = styled(AdminPanelCard)(({ theme }) => ({
    borderRadius: 6,
    background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const SoftTableContainer = styled(TableContainer)(({ theme }) => ({
    borderRadius: 6,
    border: 'none',
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0,
    },
    '& .MuiTableHead-root .MuiTableRow-root': {
        backgroundColor: '#f5f7fb',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
        borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
        color: '#64748b',
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        backgroundColor: '#f5f7fb',
    },
    '& .MuiTableBody-root .MuiTableRow-root': {
        backgroundColor: '#ffffff',
        transition: 'background-color 0.2s ease',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
        backgroundColor: '#f8fbff',
    },
    '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-root': {
        borderBottom: 'none',
    },
    '& .MuiTableBody-root .MuiTableCell-root': {
        borderBottom: '1px solid rgba(226, 232, 240, 0.75)',
        borderLeft: 'none',
        borderRight: 'none',
        fontSize: '0.92rem',
        color: theme.palette.text.primary,
        paddingTop: theme.spacing(1.6),
        paddingBottom: theme.spacing(1.6),
        verticalAlign: 'middle',
    },
}));

// --- Component ---

const BusManager = () => {
    const theme = useTheme();

    // State Variables
    const [buses, setBuses] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [formBusData, setFormBusData] = useState({ // Renamed state for clarity
        busNumber: '', capacity: '', registrationNumber: '', employeeId: '', driverName: '', contactInfo: '', gpsDeviceId: '',
    });
    const [editBusId, setEditBusId] = useState(null); // ID of bus being edited
    const [isLoading, setIsLoading] = useState(true); // Combined loading state
    const [isSubmitting, setIsSubmitting] = useState(false); // Form submission loading
    const [loadingEmployees, setLoadingEmployees] = useState(false); // Employee dropdown loading
    const [error, setError] = useState(null); // For major fetch errors
    const [openFormDialog, setOpenFormDialog] = useState(false);
    const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
    const [busToDeleteId, setBusToDeleteId] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const schoolId = localStorage.getItem('schoolId');

    // --- Data Fetching ---

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // Fetch Buses
    const fetchBuses = useCallback(async () => {
        if (!schoolId) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get('/api/buses', { headers: { schoolId } });
            setBuses(response.data || []);
        } catch (err) {
            console.error('Error fetching buses:', err);
            setError('Failed to fetch bus data. Please try refreshing the page.');
            // Keep existing snackbar mechanism for transient errors if needed
        } finally {
            setIsLoading(false);
        }
    }, [schoolId]);

    // Fetch Employees (potential drivers)
    const fetchEmployees = useCallback(async () => {
        if (!schoolId) return;
        setLoadingEmployees(true);
        try {
            const response = await axios.get('/api/employees', {
                params: { role: 'DRIVER' }, // Ensure your API supports filtering by role
                headers: { schoolId },
            });
            setEmployees(response.data || []);
        } catch (err) {
            console.error('Error fetching employees:', err);
            showSnackbar('Failed to load available drivers.', 'warning'); // Non-critical error
        } finally {
            setLoadingEmployees(false);
        }
    }, [schoolId]);

    // Initial data fetch on component mount
    useEffect(() => {
        if (schoolId) {
            fetchBuses();
            fetchEmployees(); // Fetch employees proactively
        } else {
             setError("School ID not found. Please log in again.");
             setIsLoading(false);
        }
    }, [fetchBuses, fetchEmployees, schoolId]); // Dependencies

    // --- Form Handlers ---

    const handleOpenFormDialog = (bus = null) => {
        setEditBusId(bus ? bus.busId : null);
        setFormBusData(bus ? { // Pre-fill form if editing
            busNumber: bus.busNumber || '',
            capacity: bus.capacity || '',
            registrationNumber: bus.registrationNumber || '',
            employeeId: bus.employeeId || '',
            driverName: bus.driverName || '', // This might be redundant if employeeId is set correctly
            contactInfo: bus.contactInfo || '',
            gpsDeviceId: bus.gpsDeviceId || '',
        } : { // Reset form if adding
            busNumber: '', capacity: '', registrationNumber: '', employeeId: '', driverName: '', contactInfo: '', gpsDeviceId: '',
        });
        setOpenFormDialog(true);
    };

    const handleCloseFormDialog = () => {
        setOpenFormDialog(false);
        setEditBusId(null); // Clear edit state on close
        // Optionally reset formBusData here if needed, though handleOpenFormDialog does it
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setFormBusData((prev) => ({ ...prev, [name]: value }));
    };

    const handleEmployeeSelect = (e) => {
        const selectedEmployeeId = e.target.value;
        setFormBusData((prev) => ({
            ...prev,
            employeeId: selectedEmployeeId,
            // Driver name is usually derived from employee data on the backend or display layer,
            // no need to store it separately here usually unless your API requires it.
            // driverName: selectedEmployee ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}` : '',
        }));
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null); // Clear previous form errors

        try {
            if (editBusId) {
                // Update existing bus
                await axios.put(`/api/buses/${editBusId}`, formBusData, { headers: { schoolId } });
                showSnackbar('Bus updated successfully.', 'success');
            } else {
                // Add new bus
                await axios.post('/api/buses', formBusData, { headers: { schoolId } });
                showSnackbar('Bus added successfully.', 'success');
            }
            handleCloseFormDialog(); // Close dialog on success
            await fetchBuses(); // Refresh bus list
        } catch (err) {
            console.error(`Error ${editBusId ? 'updating' : 'adding'} bus:`, err);
            showSnackbar(`Failed to ${editBusId ? 'update' : 'add'} bus. ${err.response?.data?.message || ''}`, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Delete Handlers ---

    const handleOpenDeleteConfirm = (id) => {
        setBusToDeleteId(id);
        setOpenDeleteConfirmDialog(true);
    };

    const handleCloseDeleteConfirm = () => {
        setBusToDeleteId(null);
        setOpenDeleteConfirmDialog(false);
    };

    const handleDeleteBus = async () => {
        if (!busToDeleteId) return;
        setIsSubmitting(true); // Use submitting state for delete operation as well

        try {
            await axios.delete(`/api/buses/${busToDeleteId}`, { headers: { schoolId } });
            setBuses((prevBuses) => prevBuses.filter((bus) => bus.busId !== busToDeleteId));
            showSnackbar('Bus deleted successfully.', 'success');
            handleCloseDeleteConfirm(); // Close dialog on success
        } catch (err) {
            console.error('Error deleting bus:', err);
            showSnackbar(`Failed to delete bus. ${err.response?.data?.message || ''}`, 'error');
        } finally {
            setIsSubmitting(false);
            // busToDeleteId is cleared in handleCloseDeleteConfirm
        }
    };

    // --- Render Logic ---

    const renderSkeletons = (count = 5) => (
        Array.from(new Array(count)).map((_, index) => (
            <TableRow key={`skel-${index}`}>
                <TableCell><Skeleton variant="text" width={24} /></TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 2.5 }} />
                        <Box sx={{ minWidth: 160 }}>
                            <Skeleton variant="text" width="55%" />
                            <Skeleton variant="text" width="80%" />
                        </Box>
                    </Stack>
                </TableCell>
                <TableCell><Skeleton variant="text" width={110} /></TableCell>
                <TableCell><Skeleton variant="text" width={40} /></TableCell>
                <TableCell><Skeleton variant="text" width={120} /></TableCell>
                <TableCell><Skeleton variant="rounded" width={76} height={28} sx={{ borderRadius: 6 }} /></TableCell>
                <TableCell align="right"><Skeleton variant="text" width={64} sx={{ ml: 'auto' }} /></TableCell>
            </TableRow>
        ))
    );

    const renderNoBuses = () => (
        <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            py: 6, px: 3, textAlign: 'center', bgcolor: alpha(theme.palette.grey[200], 0.3),
            borderRadius: 2, mt: 2, mx: 'auto', maxWidth: 'sm'
        }}>
            <img src={NoResultsImage} alt="No Buses Found" style={{ width: '120px', opacity: 0.6, marginBottom: theme.spacing(2) }} />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                No buses found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Get started by adding the first school bus to your fleet.
            </Typography>
            <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenFormDialog()}
            >
                Add Bus
            </Button>
        </Box>
    );


    return (
        <Box sx={{ width: '100%' }}>
            <AdminPageHero
                title="Bus Management"
                subtitle="Track buses, assign drivers, and keep fleet details updated."
                metrics={[
                    { label: 'Total Buses', value: buses.length },
                    { label: 'Assigned Drivers', value: buses.filter((bus) => bus.employeeId).length },
                    { label: 'Unassigned', value: buses.filter((bus) => !bus.employeeId).length },
                ]}
                actions={
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenFormDialog()}
                        disabled={isLoading}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        Add New Bus
                    </Button>
                }
            />

            {/* Display Major Error */}
            {error && !isLoading && (
                 <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={fetchBuses}>Retry</Button>}>
                     <AlertTitle>Error</AlertTitle>
                     {error}
                 </Alert>
            )}

            {/* Bus Table */}
            <Box sx={{ mt: 3.5 }}>
                <ListCard
                    title="Bus Fleet"
                    subtitle="Manage bus capacity, registration details, driver mapping, and GPS linkage."
                >
                    {isLoading ? (
                        <SoftTableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Bus</TableCell>
                                        <TableCell>Registration</TableCell>
                                        <TableCell>Capacity</TableCell>
                                        <TableCell>Driver</TableCell>
                                        <TableCell>GPS</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>{renderSkeletons()}</TableBody>
                            </Table>
                        </SoftTableContainer>
                    ) : buses.length > 0 ? (
                        <SoftTableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 56 }}>#</TableCell>
                                        <TableCell>Bus</TableCell>
                                        <TableCell>Registration</TableCell>
                                        <TableCell sx={{ width: 110 }}>Capacity</TableCell>
                                        <TableCell>Driver</TableCell>
                                        <TableCell sx={{ width: 120 }}>GPS</TableCell>
                                        <TableCell align="right" sx={{ width: 110 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {buses.map((bus, index) => {
                                        const driver = employees.find((e) => e.employeeId === bus.employeeId);
                                        const driverDisplayName = driver
                                            ? `${driver.firstName} ${driver.lastName}`
                                            : (bus.driverName || 'Unassigned');
                                        const contactInfoDisplay = bus.contactInfo || driver?.phone || 'No contact';

                                        return (
                                            <TableRow key={bus.busId}>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={700} color="text.secondary">
                                                        {String(index + 1).padStart(2, '0')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1.4} alignItems="center">
                                                        <Box
                                                            sx={{
                                                                width: 44,
                                                                height: 44,
                                                                borderRadius: 1.5,
                                                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                flexShrink: 0,
                                                            }}
                                                        >
                                                            <DirectionsBusIcon color="primary" fontSize="small" />
                                                        </Box>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.35 }}>
                                                                {bus.busNumber || 'Unnamed Bus'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                                Route vehicle with fleet record #{bus.busId}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {bus.registrationNumber || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                        {bus.capacity || '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.35 }}>
                                                        {driverDisplayName}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" noWrap>
                                                        {contactInfoDisplay}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={bus.gpsDeviceId ? 'GPS Linked' : 'No GPS'}
                                                        sx={{
                                                            bgcolor: bus.gpsDeviceId
                                                                ? alpha(theme.palette.info.main, 0.12)
                                                                : alpha(theme.palette.grey[500], 0.12),
                                                            color: bus.gpsDeviceId
                                                                ? theme.palette.info.main
                                                                : theme.palette.text.secondary,
                                                            fontWeight: 700,
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <Tooltip title="Edit Bus Details">
                                                            <IconButton size="small" color="primary" onClick={() => handleOpenFormDialog(bus)}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Bus">
                                                            <IconButton size="small" color="error" onClick={() => handleOpenDeleteConfirm(bus.busId)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </SoftTableContainer>
                    ) : (
                        renderNoBuses()
                    )}
                </ListCard>
            </Box>

            {/* Add/Edit Bus Dialog */}
            <Dialog open={openFormDialog} onClose={handleCloseFormDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 500 }}>{editBusId ? 'Edit Bus Details' : 'Add New Bus'}</DialogTitle>
                <Box component="form" onSubmit={handleFormSubmit} noValidate> {/* Use Box with form component */}
                    <DialogContent>
                         <Stack spacing={2.5} sx={{ pt: 1 }}> {/* Use Stack for spacing */}
                             <TextField
                                 label="Bus Number" name="busNumber" value={formBusData.busNumber} onChange={handleFieldChange}
                                 required fullWidth autoFocus size="small" InputLabelProps={{ shrink: true }}/>
                            <TextField
                                label="Capacity" name="capacity" type="number" value={formBusData.capacity} onChange={handleFieldChange}
                                required fullWidth size="small" inputProps={{ min: 1 }} InputLabelProps={{ shrink: true }}/>
                             <TextField
                                 label="Registration Number" name="registrationNumber" value={formBusData.registrationNumber} onChange={handleFieldChange}
                                 fullWidth size="small" InputLabelProps={{ shrink: true }}/>
                            <FormControl fullWidth size="small">
                                <InputLabel id="driver-select-label">Assign Driver</InputLabel>
                                <Select
                                    labelId="driver-select-label" label="Assign Driver"
                                    name="employeeId" value={formBusData.employeeId} onChange={handleEmployeeSelect}
                                >
                                     <MenuItem value=""><em>None / Unassigned</em></MenuItem> {/* Add an unassigned option */}
                                    {loadingEmployees ? (
                                        <MenuItem disabled value="">Loading Drivers...</MenuItem>
                                    ) : employees.length > 0 ? (
                                        employees.map((emp) => (
                                            <MenuItem key={emp.employeeId} value={emp.employeeId}>
                                                {emp.firstName} {emp.lastName} ({emp.employeeCode})
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled value="">No Drivers Found</MenuItem>
                                    )}
                                </Select>
                            </FormControl>
                             <TextField
                                 label="Driver Contact Info (if different from profile)" name="contactInfo" value={formBusData.contactInfo} onChange={handleFieldChange}
                                 fullWidth size="small" InputLabelProps={{ shrink: true }} helperText="Leave blank to use driver's profile contact."/>
                             <TextField
                                 label="GPS Device ID (IMEI/Tracker ID)" name="gpsDeviceId" value={formBusData.gpsDeviceId} onChange={handleFieldChange}
                                 fullWidth size="small" InputLabelProps={{ shrink: true }} helperText="Use the same ID sent by the installed GPS device."/>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}> {/* Consistent padding */}
                        <Button onClick={handleCloseFormDialog} color="inherit" variant="outlined">
                            Cancel
                        </Button>
                        <Button type="submit" color="primary" variant="contained" disabled={isSubmitting}>
                            {isSubmitting ? <CircularProgress size={22} color="inherit"/> : (editBusId ? 'Update Bus' : 'Add Bus')}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

             {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteConfirmDialog} onClose={handleCloseDeleteConfirm} maxWidth="xs">
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning"/> Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this bus? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteConfirm} color="inherit">Cancel</Button>
                    <Button onClick={handleDeleteBus} color="error" variant="contained" disabled={isSubmitting}>
                         {isSubmitting ? <CircularProgress size={22} color="inherit"/> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for Notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000} // Slightly shorter duration
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} // Centered is often preferred
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled" // Filled provides better contrast
                    elevation={6} // Add elevation
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BusManager;
